import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import HtmlEditor, { Item, Toolbar as HTMLToolbar } from "devextreme-react/html-editor";
import { LoadPanel } from "devextreme-react/load-panel";
import notify from "devextreme/ui/notify";
import { useTranslation } from "react-i18next";
import { getCurrentUser } from "../../../api/auth";
import {
    Conversation, ChatMessage, SupportUser, Ticket,
    getConversations, getChatMessages, sendChatMessage,
    getSupportUsers, getOrCreateConversation, getTickets,
} from "../../../api/support";
import { getInitials, STATUS_TEXT_KEYS, STATUS_BADGE, PRIORITY_TEXT_KEYS, PRIORITY_BADGE, PRODUCT_TEXT } from "../messages/ticket-constants";
import "./TeamChatPage.css";

export default function TeamChatPage() {
    const { t } = useTranslation();
    const user = getCurrentUser();
    const currentUserId = "usr-005";

    // Scope: intern (İç Sohbet = kişisel+grup) / personal / group / company
    const [searchParams] = useSearchParams();
    const scope = searchParams.get("scope") || "intern";
    const scopeTitle = scope === "personal" ? t("personalMessages")
        : scope === "group" ? t("groupChats")
        : scope === "company" ? t("companyChats")
        : t("internChat");
    const inScope = (c: Conversation) => {
        if (scope === "personal") return c.type === "personal";
        if (scope === "group") return c.type === "group";
        if (scope === "company") return c.type === "company";
        return c.type !== "company"; // İç Sohbet: kişisel + grup
    };

    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [activeConvId, setActiveConvId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [allUsers, setAllUsers] = useState<SupportUser[]>([]);
    const [reply, setReply] = useState("");
    const [loading, setLoading] = useState(true);
    const [msgLoading, setMsgLoading] = useState(false);
    const [showNewChat, setShowNewChat] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showTicketPicker, setShowTicketPicker] = useState(false);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [ticketSearch, setTicketSearch] = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [attachedTicket, setAttachedTicket] = useState<Ticket | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const buildTicketHtml = (ticket: Ticket) => {
        const statusKey = STATUS_TEXT_KEYS[ticket.status] || "unknown";
        const priorityKey = PRIORITY_TEXT_KEYS[ticket.priority] || "unknown";
        const product = PRODUCT_TEXT[ticket.product_code] || "";
        const statusColor = STATUS_BADGE[ticket.status] || "#6B7280";
        const priorityColor = PRIORITY_BADGE[ticket.priority] || "#9CA3AF";
        return `<div class="chat-ticket-card" data-ticket-id="${ticket.ref}">` +
            `<div class="chat-ticket-header">` +
            `<span class="chat-ticket-icon">&#x1F3AB;</span>` +
            `<strong>${ticket.ticket_code}</strong>` +
            `</div>` +
            `<div class="chat-ticket-title">${ticket.title}</div>` +
            `<div class="chat-ticket-meta">` +
            `<span style="background:${statusColor}20;color:${statusColor};padding:2px 8px;border-radius:99px;font-size:11px">${t(statusKey)}</span>` +
            `<span style="background:${priorityColor}20;color:${priorityColor};padding:2px 8px;border-radius:99px;font-size:11px">${t(priorityKey)}</span>` +
            (product ? `<span style="font-size:11px;color:#6B7280">${product}</span>` : '') +
            `</div>` +
            (ticket.customer_name ? `<div class="chat-ticket-customer">${ticket.customer_name}</div>` : '') +
            `</div>`;
    };

    const loadConversations = useCallback(async () => {
        const convs = await getConversations(currentUserId);
        setConversations(convs);
        return convs;
    }, [currentUserId]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const [convs, users] = await Promise.all([
                loadConversations(),
                getSupportUsers(),
            ]);
            setAllUsers(users);
            if (convs.length > 0) {
                setActiveConvId(convs[0].ref);
            }
            setLoading(false);
        })();
    }, [loadConversations]);

    useEffect(() => {
        if (!activeConvId) return;
        (async () => {
            setMsgLoading(true);
            const msgs = await getChatMessages(activeConvId);
            setMessages(msgs);
            setMsgLoading(false);
        })();
    }, [activeConvId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = async () => {
        if (!reply.trim() && attachments.length === 0 && !attachedTicket) return;
        if (!activeConvId) return;
        setMsgLoading(true);
        try {
            let content = reply;
            if (attachedTicket) {
                content = buildTicketHtml(attachedTicket) + content;
            }
            if (attachments.length > 0) {
                const fileHtml = attachments.map(f => {
                    const icon = f.type.startsWith("image/") ? "&#x1F5BC;" : "&#x1F4CE;";
                    const size = f.size < 1024 ? `${f.size} B` : f.size < 1048576 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / 1048576).toFixed(1)} MB`;
                    return `<div class="chat-file-card">${icon} <strong>${f.name}</strong> <span style="opacity:0.6;font-size:11px">(${size})</span></div>`;
                }).join("");
                content = content + fileHtml;
            }
            await sendChatMessage(activeConvId, content, currentUserId, user?.name || "Ben");
            setReply("");
            setAttachments([]);
            setAttachedTicket(null);
            const [msgs] = await Promise.all([
                getChatMessages(activeConvId),
                loadConversations(),
            ]);
            setMessages(msgs);
            notify(t("messageSent"), "success", 1500);
        } catch {
            notify(t("messageSendError"), "error", 2000);
        } finally {
            setMsgLoading(false);
        }
    };

    const handleOpenTicketPicker = async () => {
        if (tickets.length === 0) {
            const all = await getTickets();
            setTickets(all);
        }
        setTicketSearch("");
        setShowTicketPicker(true);
    };

    const handleAttachTicket = (ticket: Ticket) => {
        setAttachedTicket(ticket);
        setShowTicketPicker(false);
    };

    const filteredTickets = tickets.filter(tk =>
        ticketSearch === "" ||
        tk.ticket_code.toLowerCase().includes(ticketSearch.toLowerCase()) ||
        tk.title.toLowerCase().includes(ticketSearch.toLowerCase()) ||
        (tk.customer_name || "").toLowerCase().includes(ticketSearch.toLowerCase())
    );

    const handleStartNewChat = async (targetUserId: string) => {
        setShowNewChat(false);
        setLoading(true);
        try {
            const conv = await getOrCreateConversation(currentUserId, targetUserId);
            await loadConversations();
            setActiveConvId(conv.ref);
        } finally {
            setLoading(false);
        }
    };

    const getOtherParticipantName = (conv: Conversation) => {
        const idx = conv.participants.indexOf(currentUserId);
        return conv.participant_names[idx === 0 ? 1 : 0] || t("unknown");
    };

    const getOtherParticipantId = (conv: Conversation) => {
        return conv.participants.find(p => p !== currentUserId) || "";
    };

    const activeConv = conversations.find(c => c.ref === activeConvId);

    // Scope'a gore gosterilecek konusmalar ve baslik/altyazi yardimcilari
    const visibleConvs = conversations.filter(inScope);
    const isPersonal = (c: Conversation) => c.type === "personal" || !c.type;
    const convDisplayName = (c: Conversation) => isPersonal(c) ? getOtherParticipantName(c) : (c.name || t("unknown"));
    const convSubtitle = (c: Conversation) => isPersonal(c) ? t("online") : t("membersCount", { count: c.participants.length });

    // Scope degisince (veya liste yuklenince) aktif sohbet kapsamda degilse ilk gorunure gec
    useEffect(() => {
        if (!conversations.length) return;
        const vis = conversations.filter(inScope);
        if (!vis.some(c => c.ref === activeConvId)) setActiveConvId(vis[0]?.ref ?? null);
    }, [scope, conversations]);

    const formatTime = (dateStr: string) =>
        new Date(dateStr).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

    const formatDateFull = (dateStr: string) =>
        new Date(dateStr).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });

    const formatConvTime = (dateStr?: string) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        const today = new Date();
        if (d.toDateString() === today.toDateString()) return formatTime(dateStr);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (d.toDateString() === yesterday.toDateString()) return t("yesterday");
        return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
    };

    const availableUsersForNewChat = allUsers.filter(u =>
        u.ref !== currentUserId &&
        !conversations.some(c => c.participants.includes(u.ref)) &&
        (searchTerm === "" || u.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="team-chat-page">
            <LoadPanel visible={loading} showPane={false} />

            {/* ── Left: Conversation List ── */}
            <div className="team-chat-sidebar">
                <div className="team-chat-sidebar-header">
                    <h2 className="team-chat-sidebar-title">{scopeTitle}</h2>
                    <button
                        className="team-chat-new-btn"
                        onClick={() => setShowNewChat(!showNewChat)}
                        title={t("newConversation")}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </button>
                </div>

                {/* New chat user picker */}
                {showNewChat && (
                    <div className="team-chat-new-panel">
                        <input
                            className="team-chat-search"
                            placeholder={t("searchUser")}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        <div className="team-chat-user-list">
                            {availableUsersForNewChat.length === 0 ? (
                                <div className="team-chat-no-users">{t("noUsersFound")}</div>
                            ) : (
                                availableUsersForNewChat.map(u => (
                                    <button
                                        key={u.ref}
                                        className="team-chat-user-item"
                                        onClick={() => handleStartNewChat(u.ref)}
                                    >
                                        <div className="team-chat-avatar small">
                                            {getInitials(u.full_name)}
                                        </div>
                                        <span>{u.full_name}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Conversation list */}
                <div className="team-chat-conv-list">
                    {visibleConvs.length === 0 && !loading ? (
                        <div className="team-chat-empty-sidebar">
                            <p>{t("noConversations")}</p>
                        </div>
                    ) : (
                        visibleConvs.map(conv => {
                            const isActive = conv.ref === activeConvId;
                            const displayName = convDisplayName(conv);
                            const otherId = getOtherParticipantId(conv);
                            const otherUser = allUsers.find(u => u.ref === otherId);
                            const isOnline = isPersonal(conv) && otherUser?.is_active;

                            return (
                                <button
                                    key={conv.ref}
                                    className={`team-chat-conv-item ${isActive ? "active" : ""}`}
                                    onClick={() => setActiveConvId(conv.ref)}
                                >
                                    <div className="team-chat-avatar-wrap">
                                        <div className="team-chat-avatar">
                                            {getInitials(displayName)}
                                        </div>
                                        {isOnline && <span className="team-chat-online-dot" />}
                                    </div>
                                    <div className="team-chat-conv-info">
                                        <div className="team-chat-conv-top">
                                            <span className="team-chat-conv-name">{displayName}</span>
                                            <span className="team-chat-conv-time">{formatConvTime(conv.last_message_at)}</span>
                                        </div>
                                        <div className="team-chat-conv-bottom">
                                            <span className="team-chat-conv-preview">{conv.last_message || t("noMessages")}</span>
                                            {(conv.unread_count ?? 0) > 0 && (
                                                <span className="team-chat-unread">{conv.unread_count}</span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ── Right: Chat Area ── */}
            <div className="team-chat-main">
                {!activeConv ? (
                    <div className="team-chat-empty-main">
                        <div className="team-chat-empty-icon">
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <p className="text-sm font-medium mt-3">{t("selectConversation")}</p>
                        <p className="text-xs mt-1 opacity-60">{t("selectConversationDesc")}</p>
                    </div>
                ) : (
                    <>
                        {/* Chat header */}
                        <div className="team-chat-header">
                            <div className="team-chat-avatar">
                                {getInitials(convDisplayName(activeConv))}
                            </div>
                            <div>
                                <div className="team-chat-header-name">{convDisplayName(activeConv)}</div>
                                <div className="team-chat-header-status">{convSubtitle(activeConv)}</div>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="team-chat-messages">
                            {messages.length === 0 ? (
                                <div className="team-chat-empty-messages">
                                    <p>{t("noMessages")}</p>
                                    <p className="text-xs mt-1 opacity-60">{t("startConversation")}</p>
                                </div>
                            ) : (
                                <div className="team-chat-thread">
                                    {messages.map((m, idx) => {
                                        const isMe = m.sender_id === currentUserId;
                                        const showDate = idx === 0 ||
                                            new Date(messages[idx - 1].created_at).toDateString() !== new Date(m.created_at).toDateString();

                                        return (
                                            <div key={m.ref}>
                                                {showDate && (
                                                    <div className="team-chat-date-divider">
                                                        <span>{formatDateFull(m.created_at)}</span>
                                                    </div>
                                                )}
                                                <div className={`team-chat-msg-row ${isMe ? "is-me" : "is-other"}`}>
                                                    {!isMe && (
                                                        <div className="team-chat-msg-avatar">
                                                            {getInitials(m.sender_name)}
                                                        </div>
                                                    )}
                                                    <div className="team-chat-msg-bubble-wrap">
                                                        {!isMe && (
                                                            <span className="team-chat-msg-sender">{m.sender_name}</span>
                                                        )}
                                                        <div className={`team-chat-msg-bubble ${isMe ? "me" : "other"}`}>
                                                            <div dangerouslySetInnerHTML={{ __html: m.content }} />
                                                            <span className="team-chat-msg-time">{formatTime(m.created_at)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div ref={messagesEndRef} />
                                </div>
                            )}
                        </div>

                        {/* Ticket Picker Popup */}
                        {showTicketPicker && (
                            <div className="team-chat-ticket-overlay" onClick={() => setShowTicketPicker(false)}>
                                <div className="team-chat-ticket-popup" onClick={e => e.stopPropagation()}>
                                    <div className="team-chat-ticket-popup-header">
                                        <h3>{t("shareTicket")}</h3>
                                        <button className="team-chat-ticket-popup-close" onClick={() => setShowTicketPicker(false)}>&times;</button>
                                    </div>
                                    <input
                                        className="team-chat-search"
                                        placeholder={t("searchTicket")}
                                        value={ticketSearch}
                                        onChange={e => setTicketSearch(e.target.value)}
                                        autoFocus
                                    />
                                    <div className="team-chat-ticket-list">
                                        {filteredTickets.length === 0 ? (
                                            <div className="team-chat-no-users">{t("noTicketsFound")}</div>
                                        ) : (
                                            filteredTickets.map(tk => (
                                                <button
                                                    key={tk.ref}
                                                    className="team-chat-ticket-item"
                                                    onClick={() => handleAttachTicket(tk)}
                                                >
                                                    <div className="team-chat-ticket-item-left">
                                                        <span className="team-chat-ticket-code">{tk.ticket_code}</span>
                                                        <span className="team-chat-ticket-title-text">{tk.title}</span>
                                                    </div>
                                                    <div className="team-chat-ticket-item-right">
                                                        <span
                                                            className="team-chat-ticket-status"
                                                            style={{ background: `${STATUS_BADGE[tk.status]}20`, color: STATUS_BADGE[tk.status] }}
                                                        >
                                                            {t(STATUS_TEXT_KEYS[tk.status])}
                                                        </span>
                                                    </div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Composer */}
                        <div className="team-chat-composer-wrap">
                            {/* Attached ticket preview */}
                            {attachedTicket && (
                                <div className="team-chat-attached-ticket">
                                    <div className="team-chat-attached-ticket-body">
                                        <div className="team-chat-attached-ticket-header">
                                            <span>&#x1F3AB;</span>
                                            <strong>{attachedTicket.ticket_code}</strong>
                                            <span
                                                className="team-chat-attached-ticket-status"
                                                style={{
                                                    background: `${STATUS_BADGE[attachedTicket.status]}20`,
                                                    color: STATUS_BADGE[attachedTicket.status],
                                                }}
                                            >
                                                {t(STATUS_TEXT_KEYS[attachedTicket.status])}
                                            </span>
                                        </div>
                                        <div className="team-chat-attached-ticket-title">{attachedTicket.title}</div>
                                        {attachedTicket.customer_name && (
                                            <div className="team-chat-attached-ticket-customer">{attachedTicket.customer_name}</div>
                                        )}
                                    </div>
                                    <button
                                        className="team-chat-attached-ticket-remove"
                                        onClick={() => setAttachedTicket(null)}
                                        title={t("remove")}
                                    >&times;</button>
                                </div>
                            )}
                            {/* Attachment preview */}
                            {attachments.length > 0 && (
                                <div className="team-chat-attachment-preview">
                                    {attachments.map((f, i) => (
                                        <span key={i} className="team-chat-attachment-chip">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                            </svg>
                                            {f.name}
                                            <button
                                                className="team-chat-attachment-remove"
                                                onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                                            >&times;</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="team-chat-composer">
                                <button
                                    className="team-chat-ticket-btn"
                                    onClick={handleOpenTicketPicker}
                                    title={t("shareTicket")}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                    </svg>
                                </button>
                                <label className="team-chat-attach-btn" title={t("attachFile")}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
                                        className="hidden"
                                        onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files || [])])}
                                    />
                                </label>
                                <div className="team-chat-editor-wrap">
                                    <HtmlEditor height={80} value={reply} onValueChanged={e => setReply(e.value)}>
                                        <HTMLToolbar>
                                            <Item name="bold" /><Item name="italic" /><Item name="underline" />
                                            <Item name="separator" /><Item name="link" />
                                        </HTMLToolbar>
                                    </HtmlEditor>
                                </div>
                                <button
                                    onClick={handleSend}
                                    disabled={msgLoading || (!reply.trim() && attachments.length === 0 && !attachedTicket)}
                                    className="team-chat-send-btn"
                                    title={t("sendMessage")}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
